import {
    Assets,
    type BitmapText,
    ColorMatrixFilter,
    type Graphics,
    Rectangle,
    type Texture,
} from "pixi.js";
import { OutlineFilter } from "pixi-filters";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMapObjectInteractions } from "../hooks/useMapObjectInteractions";
import { useScene } from "../hooks/useScene";
import { ACTIVE_INTERACTION_COLOR } from "../interaction";
import type { DroneMapObject, LayerId, MapItemHandler } from "../types";
import OutlinedBitmapText from "./OutlinedBitmapText";

const DRONE_E12_ASSET_URL = new URL("./assets/droneE12.png", import.meta.url)
    .href;
const DRONE_E12_SILHOUETTE_ASSET_URL = new URL(
    "./assets/droneE12_silhouette.png",
    import.meta.url,
).href;
const DRONE_E13_ASSET_URL = new URL("./assets/droneE13.png", import.meta.url)
    .href;
const DRONE_E13_SILHOUETTE_ASSET_URL = new URL(
    "./assets/droneE13_silhouette.png",
    import.meta.url,
).href;

const DRONE_WORLD_HEIGHT = 1.2;
const ID_FONT_SIZE_WORLD = 0.4;
const STATUS_FONT_SIZE_WORLD = 0.25;
const ID_OFFSET_Y_WORLD = -0.2;
const STATUS_OFFSET_Y_WORLD = 0.2;
const OFFLINE_TEXT_COLOR = "#888888";
const OUTLINE_THICKNESS = 3;

const OFFLINE_FILTER = new ColorMatrixFilter();
OFFLINE_FILTER.saturate(-0.8);

const OUTLINE_FILTER = new OutlineFilter({
    color: ACTIVE_INTERACTION_COLOR,
    thickness: OUTLINE_THICKNESS,
    knockout: true,
});

interface DroneTextures {
    e12: Texture;
    e12Silhouette: Texture;
    e13: Texture;
    e13Silhouette: Texture;
}

interface DroneObjectProps {
    item: DroneMapObject;
    layerId: LayerId;
    selected: boolean;
    onItemClick?: MapItemHandler<DroneMapObject>;
    onItemHover?: MapItemHandler<DroneMapObject>;
    onItemLeave?: MapItemHandler<DroneMapObject>;
}

let droneTexturesPromise: Promise<DroneTextures> | undefined;

function getDroneTextures(): Promise<DroneTextures> {
    if (!droneTexturesPromise) {
        droneTexturesPromise = Promise.all([
            Assets.load<Texture>(DRONE_E12_ASSET_URL),
            Assets.load<Texture>(DRONE_E12_SILHOUETTE_ASSET_URL),
            Assets.load<Texture>(DRONE_E13_ASSET_URL),
            Assets.load<Texture>(DRONE_E13_SILHOUETTE_ASSET_URL),
        ]).then(([e12, e12Silhouette, e13, e13Silhouette]) => ({
            e12,
            e12Silhouette,
            e13,
            e13Silhouette,
        }));
    }

    return droneTexturesPromise;
}

function useDroneTextures(): DroneTextures | null {
    const [textures, setTextures] = useState<DroneTextures | null>(null);

    useEffect(() => {
        let mounted = true;

        void getDroneTextures().then((loadedTextures) => {
            if (mounted) setTextures(loadedTextures);
        });

        return () => {
            mounted = false;
        };
    }, []);

    return textures;
}

function selectDroneTextures(
    item: DroneMapObject,
    textures: DroneTextures,
): { base: Texture; silhouette: Texture } {
    if (item.model === "E13") {
        return { base: textures.e13, silhouette: textures.e13Silhouette };
    }

    return { base: textures.e12, silhouette: textures.e12Silhouette };
}

function useDroneGeometry(item: DroneMapObject, texture: Texture | null) {
    const { coordinates } = useScene();

    return useMemo(() => {
        if (!texture) return null;

        const center = coordinates.worldToScene({ x: item.x, y: item.y });
        const height = coordinates.worldLengthToScene(DRONE_WORLD_HEIGHT);
        const width = height * (texture.width / texture.height);
        const rotation = -item.yaw * (Math.PI / 180);
        const cos = Math.abs(Math.cos(rotation));
        const sin = Math.abs(Math.sin(rotation));
        const rotatedWidth = width * cos + height * sin;
        const rotatedHeight = width * sin + height * cos;
        const idFontSize = coordinates.worldLengthToScene(ID_FONT_SIZE_WORLD);
        const statusFontSize = coordinates.worldLengthToScene(
            STATUS_FONT_SIZE_WORLD,
        );
        const labelWidth = item.id.length * idFontSize;
        const boundsWidth = Math.max(rotatedWidth, labelWidth);
        const boundsHeight = Math.max(rotatedHeight, height + statusFontSize);

        return {
            center,
            width,
            height,
            rotation,
            bounds: new Rectangle(
                center.x - boundsWidth / 2,
                center.y - boundsHeight / 2,
                boundsWidth,
                boundsHeight,
            ),
            idFontSize,
            statusFontSize,
            idOffsetY: coordinates.worldLengthToScene(ID_OFFSET_Y_WORLD),
            statusOffsetY: coordinates.worldLengthToScene(
                STATUS_OFFSET_Y_WORLD,
            ),
        };
    }, [coordinates, item.id, item.x, item.y, item.yaw, texture]);
}

function DroneIdLabel({
    item,
    fontSize,
}: {
    item: DroneMapObject;
    fontSize: number;
}) {
    const { labelFontReady } = useScene();

    if (!labelFontReady) return null;

    return (
        <OutlinedBitmapText
            text={item.id}
            fontSize={fontSize}
            fill={item.isOnline ? "#ffffff" : OFFLINE_TEXT_COLOR}
            outlineOffset={1.5}
        />
    );
}

function getBatteryColor(item: DroneMapObject): string {
    if (!item.isOnline) return OFFLINE_TEXT_COLOR;
    if (item.batteryPercentage >= 35) return "#28a745";
    if (item.batteryPercentage >= 20) return "#ffc107";
    return "#dc3545";
}

const CHARGING_BOLT_COLOR = 0xfff000;
const BOLT_POINTS = [
    [-65, 13],
    [-3, 13],
    [-50, 95],
    [65, -13],
    [3, -13],
    [50, -95],
] as const;

const BOLT_MIN_X = Math.min(...BOLT_POINTS.map(([x]) => x));
const BOLT_MAX_X = Math.max(...BOLT_POINTS.map(([x]) => x));
const BOLT_MIN_Y = Math.min(...BOLT_POINTS.map(([, y]) => y));
const BOLT_MAX_Y = Math.max(...BOLT_POINTS.map(([, y]) => y));
const BOLT_WIDTH = BOLT_MAX_X - BOLT_MIN_X;
const BOLT_HEIGHT = BOLT_MAX_Y - BOLT_MIN_Y;
const BOLT_CENTER_Y = (BOLT_MIN_Y + BOLT_MAX_Y) / 2;

function BatteryStatus({
    item,
    fontSize,
}: {
    item: DroneMapObject;
    fontSize: number;
}) {
    const { labelFontReady } = useScene();

    if (!labelFontReady) return null;

    return <ReadyBatteryStatus item={item} fontSize={fontSize} />;
}

function ReadyBatteryStatus({
    item,
    fontSize,
}: {
    item: DroneMapObject;
    fontSize: number;
}) {
    const batteryText = `${Math.round(item.batteryPercentage)}%`;
    const text = item.isCharging ? ` ${batteryText}` : batteryText;
    const fill = getBatteryColor(item);
    const textRef = useRef<BitmapText | null>(null);

    const [textMetrics, setTextMetrics] = useState({
        width: 0,
        height: 0,
        visualCenterY: 0,
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: Remeasure the BitmapText when inputs affecting its rendered bounds change.
    useEffect(() => {
        const textObject = textRef.current;
        if (!textObject) return;

        const bounds = textObject.getLocalBounds();

        setTextMetrics({
            width: bounds.width,
            height: bounds.height,
            visualCenterY: bounds.y + bounds.height / 2,
        });
    }, [fontSize, text]);

    const boltScale = textMetrics.height / BOLT_HEIGHT;
    const boltWidth = BOLT_WIDTH * boltScale;
    const chargingWidth = item.isCharging ? boltWidth : 0;
    const left = -(textMetrics.width + chargingWidth) / 2;

    const drawBolt = useCallback(
        (graphics: Graphics) => {
            graphics.clear();

            graphics.poly(
                BOLT_POINTS.flatMap(([x, y]) => [
                    (x - BOLT_MIN_X) * boltScale,
                    (y - BOLT_CENTER_Y) * boltScale,
                ]),
            );

            graphics.fill({ color: CHARGING_BOLT_COLOR });
        },
        [boltScale],
    );

    return (
        <>
            {item.isCharging && (
                <pixiGraphics
                    x={left}
                    y={-textMetrics.visualCenterY}
                    eventMode="none"
                    draw={drawBolt}
                />
            )}

            <OutlinedBitmapText
                ref={textRef}
                text={text}
                fontSize={fontSize}
                fill={fill}
                anchor={{ x: 0, y: 0.5 }}
                x={left + chargingWidth}
            />
        </>
    );
}

const DroneObject = ({
    item,
    layerId,
    selected,
    onItemClick,
    onItemHover,
    onItemLeave,
}: DroneObjectProps) => {
    const textures = useDroneTextures();
    const droneTextures = textures ? selectDroneTextures(item, textures) : null;
    const geometry = useDroneGeometry(item, droneTextures?.base ?? null);
    const interaction = useMapObjectInteractions({
        item,
        layerId,
        onItemClick,
        onItemHover,
        onItemLeave,
    });

    if (!droneTextures || !geometry) return null;

    const active = interaction.hovered || selected;

    return (
        <pixiContainer
            label="drone-culling-unit"
            cullable
            cullableChildren={false}
            cullArea={geometry.bounds}
        >
            <pixiContainer x={geometry.center.x} y={geometry.center.y}>
                <pixiSprite
                    texture={droneTextures.base}
                    width={geometry.width}
                    height={geometry.height}
                    rotation={geometry.rotation}
                    anchor={{ x: 0.5, y: 0.5 }}
                    filters={item.isOnline ? undefined : [OFFLINE_FILTER]}
                    eventMode={interaction.eventMode}
                    cursor={interaction.cursor}
                    onPointerOver={interaction.onPointerOver}
                    onPointerOut={interaction.onPointerOut}
                    onPointerTap={interaction.onPointerTap}
                />
                {active && (
                    <DroneOutline
                        texture={droneTextures.silhouette}
                        geometry={geometry}
                    />
                )}
                <pixiContainer y={geometry.idOffsetY} eventMode="none">
                    <DroneIdLabel item={item} fontSize={geometry.idFontSize} />
                </pixiContainer>
                <pixiContainer y={geometry.statusOffsetY} eventMode="none">
                    <BatteryStatus
                        item={item}
                        fontSize={geometry.statusFontSize}
                    />
                </pixiContainer>
            </pixiContainer>
        </pixiContainer>
    );
};

function DroneOutline({
    texture,
    geometry,
}: {
    texture: Texture;
    geometry: NonNullable<ReturnType<typeof useDroneGeometry>>;
}) {
    return (
        <pixiSprite
            texture={texture}
            width={geometry.width}
            height={geometry.height}
            rotation={geometry.rotation}
            anchor={{ x: 0.5, y: 0.5 }}
            filters={[OUTLINE_FILTER]}
            eventMode="none"
        />
    );
}

export default memo(DroneObject, (prev, next) => {
    return (
        prev.item === next.item &&
        prev.layerId === next.layerId &&
        prev.selected === next.selected &&
        prev.onItemClick === next.onItemClick &&
        prev.onItemHover === next.onItemHover &&
        prev.onItemLeave === next.onItemLeave
    );
});
